import { ImgElement } from '@axinom/mosaic-ui';
import * as lscache from 'lscache';
import React, { useEffect, useState } from 'react';
import { ensureError } from '../../../common/errors/ensure-error';
import logo from '../../../images/AP_logo.png';
import type { Tenant } from '../../../models/tenant';
import { authStateStore } from '../../../state-store';
import {
  authenticateBySSOToken,
  getSSOToken,
  getTenantsBySSOToken,
  setRequestFailedState,
} from '../../../util/ssoTokenUtils';
import classes from './TenantSelector.module.scss';

export const TenantSelector: React.FC = () => {
  const [renderComponent, setRenderComponent] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<Tenant>();
  const [tenantList, setTenantList] = useState<Tenant[]>([]);
  const switchedFromTenant = lscache.get('switchedFromTenantId');
  const [tenantAdminName, setTenantAdminName] = useState<string>();

  const handleSignIn = async (tenantId: string): Promise<void> => {
    let accessToken: string | undefined;
    try {
      accessToken = await authenticateBySSOToken(tenantId);
    } catch (e) {
      const error = ensureError(e);
      // The request to the env-admin API has failed
      setRequestFailedState(true, error.toString());
    }

    if (accessToken !== undefined) {
      authStateStore.setState({
        tenantId: tenantId,
        accessToken: accessToken,
      });
      lscache.remove('switchedFromTenantId');
    } else {
      // Some validation on the BE has failed (i.e. no tenants associated, SSO token not verified, etc.)
      setRequestFailedState(false);
    }
  };

  useEffect(() => {
    (async (): Promise<void> => {
      const ssoToken = await getSSOToken();

      if (ssoToken.token !== undefined) {
        let allowedTenantList: Tenant[] | undefined;
        allowedTenantList = await getTenantsBySSOToken(ssoToken.token);

        setTenantAdminName(lscache.get('tenantAdminName'));

        // Sign in to the admin portal straightaway if there's only one tenant
        // and if the user is not coming through Switch Tenant option.
        if (
          allowedTenantList !== undefined &&
          allowedTenantList.length === 1 &&
          switchedFromTenant === null
        ) {
          await handleSignIn(allowedTenantList[0].tenantId);
        } else if (
          allowedTenantList !== undefined &&
          allowedTenantList.length > 0
        ) {
          if (
            switchedFromTenant !== null &&
            switchedFromTenant !== 'not_available' // this value is set if for some reason the currentTenant is not available anymore.
          ) {
            // make the array mutable to execute the splice() operation.
            allowedTenantList = [...allowedTenantList];
            const removedTenant = allowedTenantList.splice(
              allowedTenantList.findIndex(
                (tenant) => tenant.tenantId === switchedFromTenant,
              ),
              1,
            );
            setCurrentTenant(removedTenant[0]);
          }
          setTenantList(allowedTenantList);
          setRenderComponent(true);
        }
      } else {
        setRequestFailedState(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form className={classes.container}>
      <div className={classes.wrapper}>
        {renderComponent && (
          <div className={classes.box}>
            <div className={classes.logo}>
              <ImgElement src={logo} alt="Axinom Logo" />
            </div>
            <div className={classes.tenantSelector}>
              {switchedFromTenant !== null ? 'Switch Tenant' : 'Sign In'}
            </div>
            {switchedFromTenant === 'not_available' ? (
              <div className={classes.subText}>
                The previously selected tenant is no longer available. Please
                select a different tenant to proceed.
              </div>
            ) : switchedFromTenant === null ? (
              <div className={classes.subText}>
                Hello <strong>{tenantAdminName}</strong>, please select a tenant
                to proceed.
              </div>
            ) : (
              <div className={classes.subText}>
                Please select a tenant to switch into.
              </div>
            )}

            <div className={classes.scroll}>
              {currentTenant && (
                <div
                  className={classes.currentTenant}
                  onClick={() => handleSignIn(currentTenant.tenantId)}
                >
                  <div className={classes.tenantName}>
                    {currentTenant?.name}
                  </div>
                  <div className={classes.tenantId}>
                    {currentTenant?.tenantId}
                  </div>
                </div>
              )}
              {[...tenantList]
                ?.sort((a, b) => {
                  return a.name
                    .toLowerCase()
                    .localeCompare(b.name.toLowerCase());
                })
                .map((tenant) => {
                  return (
                    <div
                      onClick={() => handleSignIn(tenant.tenantId)}
                      key={tenant.tenantId}
                      className={classes.tenant}
                      data-test-id={`select-tenant:${tenant.tenantId}`}
                    >
                      <div className={classes.tenantName}>{tenant.name}</div>
                      <div className={classes.tenantId}>{tenant.tenantId}</div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </form>
  );
};
